import React from 'react';
import { Typography } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

import { gray3, gray5, green, statusTextError, statusTextWarning } from 'utils/color-styles';
import { VolunteerRewardApprovalStatus } from 'generated/graphql';

import { FilterStatus } from './Filters';

const commonIconStyles = {
  backgroundColor: 'white',
  position: 'absolute',
  left: -14,
  top: -3,
  borderColor: 'white',
  border: 3,
  width: 29,
  height: 29,
} as const;

const numberCircleStyles = {
  position: 'absolute',
  left: -15,
  top: -3,
  width: 30,
  height: 30,
  borderRadius: 15,
  backgroundColor: gray3,
  border: '3px solid white',
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
} as const;

interface StatusConfig {
  color: string;
  Icon: typeof CheckCircleIcon | typeof ErrorIcon | typeof WarningIcon;
}

const statusConfigs: Record<NonNullable<FilterStatus>, StatusConfig> = {
  [VolunteerRewardApprovalStatus.Approved]: {
    color: green,
    Icon: CheckCircleIcon,
  },
  [VolunteerRewardApprovalStatus.Rejected]: {
    color: statusTextError,
    Icon: ErrorIcon,
  },
  [VolunteerRewardApprovalStatus.Pending]: {
    color: gray3,
    Icon: CheckCircleIcon,
  },
  NON_DISBURSABLE: {
    color: statusTextWarning,
    Icon: WarningIcon,
  },
};

export const StepItem = ({
  title,
  children,
  checked,
  stepNumber,
  end,
  status,
}: {
  title: string;
  children: JSX.Element;
  checked?: boolean;
  stepNumber?: number;
  end?: boolean;
  status?: FilterStatus;
}) => {
  const getStatusColor = () => {
    if (!status) {
      return checked ? green : undefined;
    }
    return statusConfigs[status].color;
  };

  const getStatusIcon = () => {
    if (!status) {
      if (checked) {
        const { Icon } = statusConfigs[VolunteerRewardApprovalStatus.Approved];
        return <Icon style={{ ...commonIconStyles, color: green }} />;
      }
      if (!stepNumber) {
        return null;
      }
      return (
        <div style={numberCircleStyles}>
          <Typography component="p" style={{ textAlign: 'center', color: 'white' }}>
            {stepNumber}
          </Typography>
        </div>
      );
    }

    const { Icon, color } = statusConfigs[status];
    return <Icon style={{ ...commonIconStyles, color }} />;
  };

  return (
    <div
      style={{
        paddingLeft: 32,
        borderLeft: end ? 0 : `1px solid ${gray5}`,
        paddingBottom: end ? 0 : 40,
        position: 'relative',
      }}
    >
      {getStatusIcon()}
      <Typography variant="h6" style={{ marginBottom: 12, color: getStatusColor() }}>
        {title}
      </Typography>
      {children}
    </div>
  );
};

export const StepContentItem = ({
  title,
  content,
}: {
  title: string;
  content?: string | null | number;
}) => (
  <div style={{ flexDirection: 'row', display: 'flex', marginBottom: 8 }}>
    <Typography variant="body1" style={{ fontWeight: 500, width: 'fit-content' }}>
      {title}
    </Typography>
    <Typography variant="body1" style={{ marginLeft: 2, width: 'fit-content' }}>
      {content ?? '-'}
    </Typography>
  </div>
);
